import api from './api';
import { Product } from '../types';

type ListResponse = { results: Product[]; count: number };

// Añadir normalización de tipos numéricos
const toNumber = (v: any): number => {
  if (typeof v === 'number') return v;
  if (typeof v === 'string') {
    const n = parseFloat(v);
    return isNaN(n) ? 0 : n;
  }
  return 0;
};

const normalizeProduct = (p: any): Product => {
  return {
    ...p,
    price: toNumber(p?.price),
    discount_percentage: typeof p?.discount_percentage === 'number' ? p.discount_percentage : (parseInt(p?.discount_percentage) || 0),
    stock: typeof p?.stock === 'string' ? parseInt(p.stock) : (p?.stock ?? 0),
    sku: p?.sku || '',
    low_stock_threshold: typeof p?.low_stock_threshold === 'string' ? parseInt(p.low_stock_threshold) : (p?.low_stock_threshold ?? 5),
    ingredients: p?.ingredients || '',
    usage_instructions: p?.usage_instructions || '',
    benefits: p?.benefits || '',
    size: p?.size || '',
    hair_type: p?.hair_type || '',
  } as Product;
};

const normalizeListResponse = (data: any): ListResponse => {
  if (data && Array.isArray(data.results)) {
    const normalized = data.results.map(normalizeProduct);
    return { results: normalized, count: typeof data.count === 'number' ? data.count : normalized.length };
  }
  if (Array.isArray(data)) {
    const normalized = (data as any[]).map(normalizeProduct);
    return { results: normalized, count: normalized.length };
  }
  return { results: [], count: 0 };
};

export const productService = {
  // Get all products
  getAll: async (params?: {
    categories?: string;
    categories__slug?: string;
    search?: string;
    min_price?: number;
    max_price?: number;
    is_active?: boolean;
    ordering?: string;
    page_size?: number;  // OPTIMIZACIÓN: Permitir controlar tamaño de página
  }): Promise<ListResponse> => {
    const response = await api.get('products/products/', { params });
    return normalizeListResponse(response.data);
  },

  // Get product by slug
  getBySlug: async (slug: string): Promise<Product> => {
    const response = await api.get(`products/products/${slug}/`);
    return normalizeProduct(response.data);
  },

  // Get products by category (slug)
  getByCategory: async (categorySlug: string): Promise<ListResponse> => {
    const response = await api.get('products/products/', { params: { categories__slug: categorySlug } });
    return normalizeListResponse(response.data);
  },

  // Get featured products
  getFeatured: async (): Promise<ListResponse> => {
    const response = await api.get('products/products/', { params: { is_featured: true } });
    return normalizeListResponse(response.data);
  },

  // Get best selling products
  getBestSelling: async (): Promise<ListResponse> => {
    const response = await api.get('products/products/', { params: { is_best_selling: true } });
    return normalizeListResponse(response.data);
  },

  // Create product (admin only)
  create: async (productData: Partial<Product>) => {
    const response = await api.post('products/products/', productData);
    return response.data;
  },

  // Update product (admin only)
  update: async (slug: string, productData: Partial<Product>) => {
    const response = await api.patch(`products/products/${slug}/`, productData);
    return response.data;
  },

  // Delete product (admin only)
  delete: async (slug: string) => {
    const response = await api.delete(`products/products/${slug}/`);
    return response.data;
  },
};